import type { DiscoveryMediaItem, DiscoveryMediaLinks, DiscoveryPlatform } from '@discovr/types';
import { chunks } from '../chunks';
import { MEDIA_LINKS_BATCH_MAX, MEDIA_NOT_SIGNED } from '../constants';

interface PendingSign {
  item: DiscoveryMediaItem;
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}

export type SignMedia = (item: DiscoveryMediaItem) => Promise<string>;

const PLATFORMS: ReadonlySet<string> = new Set<DiscoveryPlatform>(['youtube', 'instagram']);

/** Go rejects a whole batch with a 400 when one item is invalid, so the same rules run before anything is sent. */
export function isSignable(item: DiscoveryMediaItem): boolean {
  if (item.platform && !PLATFORMS.has(item.platform)) return false;
  if (item.kind === 'reel') return Boolean(item.platformId && item.postId);
  return Boolean(item.url || item.platformId);
}

const asError = (error: unknown) => (error instanceof Error ? error : new Error(MEDIA_NOT_SIGNED));

export function createMediaSigner(
  signBatch: (items: DiscoveryMediaItem[]) => Promise<DiscoveryMediaLinks>,
): SignMedia {
  let queue: PendingSign[] = [];
  const settle = (batch: readonly PendingSign[]) =>
    signBatch(batch.map((pending) => pending.item)).then(
      (signed) =>
        batch.forEach((pending, i) => {
          const token = signed.tokens[i];
          if (token) pending.resolve(token);
          else pending.reject(new Error(MEDIA_NOT_SIGNED));
        }),
      (error: unknown) => batch.forEach((pending) => pending.reject(asError(error))),
    );
  const flush = () => {
    const taken = queue;
    queue = [];
    chunks(taken, MEDIA_LINKS_BATCH_MAX).forEach((batch) => void settle(batch));
  };
  return (item) =>
    new Promise<string>((resolve, reject) => {
      if (!isSignable(item)) {
        reject(new Error(MEDIA_NOT_SIGNED));
        return;
      }
      if (!queue.length) setTimeout(flush, 0);
      queue.push({ item, resolve, reject });
    });
}
